import type { RouteDefinition } from '../routes';
import { siteConfig, toAbsoluteResourceUrl, toAbsoluteUrl } from '../config/site';

export function applyRouteMeta(route: RouteDefinition): void {
  const fullTitle = `${route.title} | ${siteConfig.siteName}`;
  const canonicalUrl = toAbsoluteUrl(route.canonicalPath);
  const socialImageUrl = toAbsoluteResourceUrl(route.socialImage);
  const hasShareState = new URLSearchParams(window.location.search).has('d');
  document.title = fullTitle;

  const descriptionTag = ensureElement<HTMLMetaElement>('meta[name="description"]', 'meta');
  descriptionTag.setAttribute('name', 'description');
  descriptionTag.setAttribute('content', route.description);

  const canonicalTag = ensureElement<HTMLLinkElement>('link[rel="canonical"]', 'link');
  canonicalTag.setAttribute('rel', 'canonical');
  canonicalTag.setAttribute('href', canonicalUrl);

  const robotsTag = ensureElement<HTMLMetaElement>('meta[name="robots"]', 'meta');
  robotsTag.setAttribute('name', 'robots');
  robotsTag.setAttribute(
    'content',
    hasShareState ? 'noindex,follow,max-image-preview:large' : 'index,follow,max-image-preview:large'
  );

  const ogType = ensureElement<HTMLMetaElement>('meta[property="og:type"]', 'meta');
  ogType.setAttribute('property', 'og:type');
  ogType.setAttribute('content', 'website');

  const ogSiteName = ensureElement<HTMLMetaElement>('meta[property="og:site_name"]', 'meta');
  ogSiteName.setAttribute('property', 'og:site_name');
  ogSiteName.setAttribute('content', siteConfig.siteName);

  const ogTitle = ensureElement<HTMLMetaElement>('meta[property="og:title"]', 'meta');
  ogTitle.setAttribute('property', 'og:title');
  ogTitle.setAttribute('content', fullTitle);

  const ogDescription = ensureElement<HTMLMetaElement>('meta[property="og:description"]', 'meta');
  ogDescription.setAttribute('property', 'og:description');
  ogDescription.setAttribute('content', route.description);

  const ogUrl = ensureElement<HTMLMetaElement>('meta[property="og:url"]', 'meta');
  ogUrl.setAttribute('property', 'og:url');
  ogUrl.setAttribute('content', canonicalUrl);

  const ogImage = ensureElement<HTMLMetaElement>('meta[property="og:image"]', 'meta');
  ogImage.setAttribute('property', 'og:image');
  ogImage.setAttribute('content', socialImageUrl);

  const twitterCard = ensureElement<HTMLMetaElement>('meta[name="twitter:card"]', 'meta');
  twitterCard.setAttribute('name', 'twitter:card');
  twitterCard.setAttribute('content', 'summary_large_image');

  const twitterTitle = ensureElement<HTMLMetaElement>('meta[name="twitter:title"]', 'meta');
  twitterTitle.setAttribute('name', 'twitter:title');
  twitterTitle.setAttribute('content', fullTitle);

  const twitterDescription = ensureElement<HTMLMetaElement>('meta[name="twitter:description"]', 'meta');
  twitterDescription.setAttribute('name', 'twitter:description');
  twitterDescription.setAttribute('content', route.description);

  const twitterImage = ensureElement<HTMLMetaElement>('meta[name="twitter:image"]', 'meta');
  twitterImage.setAttribute('name', 'twitter:image');
  twitterImage.setAttribute('content', socialImageUrl);
}

function ensureElement<T extends HTMLElement>(selector: string, tagName: string): T {
  const existing = document.querySelector(selector);
  if (existing) {
    return existing as T;
  }

  const element = document.createElement(tagName) as T;
  document.head.appendChild(element);
  return element;
}
